import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const NAV_LINKS = [
  { to: '/foundations', label: 'Foundations' },
  { to: '/components', label: 'Components' },
  { to: '/patterns', label: 'Patterns' },
  { to: '/resources', label: 'Resources' },
] as const;

const linkClassName =
  'text-osrs-yellow hover:text-osrs-gold uppercase text-sm font-medium tracking-wide transition-colors duration-300';

const Navbar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Close the overlay when the route changes so navigation doesn't leave it open.
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="bg-osrs-dark-brown border-b-3 border-osrs-gold">
      <div className="container mx-auto py-4 px-4">
        <div className="flex justify-between items-center gap-3 min-w-0">
          <div className="flex items-center min-w-0">
            <Link to="/" className="flex items-center min-w-0">
              <div className="flex items-center bg-gradient-to-r from-osrs-dark-brown to-black px-3 sm:px-4 py-2 rounded-lg shadow-lg border-2 border-osrs-gold min-w-0">
                <div className="text-osrs-gold font-bold text-xl sm:text-2xl uppercase tracking-wider mr-2 flex-shrink-0">
                  OSRS
                </div>
                <div className="text-osrs-yellow text-base sm:text-lg font-medium truncate">
                  Design System
                </div>
              </div>
            </Link>
            <div className="hidden md:flex items-center ml-6">
              <div className="h-8 w-px bg-gradient-to-b from-osrs-gold via-osrs-yellow to-osrs-gold opacity-50 mx-4"></div>
              <div className="flex items-center group">
                <span className="text-osrs-yellow text-sm mr-2">by</span>
                <a
                  href="https://milkbar.framer.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-osrs-gold hover:text-osrs-yellow transition-colors duration-300 text-sm font-medium group-hover:scale-105 transform"
                >
                  Milk Bar Design
                </a>
              </div>
            </div>
          </div>

          {/* Desktop primary nav: hidden on small screens to prevent overflow */}
          <nav className="hidden md:block ml-8" aria-label="Primary">
            <ul className="flex gap-8">
              {NAV_LINKS.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className={linkClassName}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Mobile menu toggle */}
          <button
            type="button"
            className="md:hidden flex-shrink-0 inline-flex items-center justify-center w-11 h-11 border-2 border-osrs-gold rounded bg-gradient-to-r from-osrs-dark-brown to-black text-osrs-gold hover:text-osrs-yellow transition-colors duration-300"
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-primary-nav"
            aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            onClick={() => setIsMobileMenuOpen((open) => !open)}
          >
            {isMobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {isMobileMenuOpen && (
          <nav
            id="mobile-primary-nav"
            className="md:hidden mt-4 pt-4 border-t-2 border-osrs-border"
            aria-label="Primary"
          >
            <ul className="flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className={`${linkClassName} block py-3 px-2 rounded hover:bg-osrs-panel`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li className="pt-2 mt-1 border-t border-osrs-border">
                <a
                  href="https://milkbar.framer.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block py-3 px-2 text-osrs-gold hover:text-osrs-yellow text-sm font-medium transition-colors duration-300"
                >
                  by Milk Bar Design
                </a>
              </li>
            </ul>
          </nav>
        )}
      </div>
    </div>
  );
};

export default Navbar;
